import { useState } from 'react'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { PageHeader } from '@/app/layouts/PageHeader'
import { cn } from '@/lib/cn'
import type { CurrencyCode } from '@/lib/money'
import { ACCOUNT_OPTIONS } from '@/features/onboarding/onboarding-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import select from '@/features/onboarding/SelectCard.module.css'
import { BRAND } from '@/brand/brand'

const REASONS = [
  {
    icon: 'savings',
    title: 'It stays your money',
    body: 'When you activate it, the $50 lands in your new account. Nothing is ever deducted from it.',
  },
  {
    icon: 'verified_user',
    title: 'Opening is free',
    body: 'Identity checks, sanctions screening and ongoing monitoring are on us.',
  },
  {
    icon: 'account_balance',
    title: 'Local details in your name',
    body: 'A real CLABE, IBAN or routing number issued to you, not a pooled account.',
  },
  {
    icon: 'schedule',
    title: 'You choose when',
    body: 'Your details are ready now. The account goes live with that first deposit.',
  },
]

const AFTER = [
  { label: 'Monthly fee', value: 'Free' },
  { label: 'Minimum balance', value: 'None' },
  { label: 'Money in', value: 'Free' },
  { label: 'Sending abroad', value: '1.0%' },
  { label: 'Between your own accounts', value: 'No fee' },
  { label: `To another ${BRAND.name} user`, value: 'No fee' },
  { label: 'FX spread when converting', value: 'from 0.6%' },
]

export function OpenAccountScreen() {
  const [currency, setCurrency] = useState<CurrencyCode>('MXN')
  const option = ACCOUNT_OPTIONS.find((item) => item.currency === currency)

  return (
    <>
      <PageHeader
        title="Open an account"
        subtitle="Free to open · nothing charged today · it goes live with a first $50 deposit, which stays yours"
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Which account do you want?
            </Text>
            <div className={`${select.options} pb-mt-4`}>
              {ACCOUNT_OPTIONS.map((item) => (
                <button
                  key={item.currency}
                  type="button"
                  className={cn(select.option, item.currency === currency && select.selected)}
                  onClick={() => setCurrency(item.currency)}
                  aria-pressed={item.currency === currency}
                >
                  <CurrencyBadge currency={item.currency} size={36} />
                  <Stack gap={1}>
                    <Text variant="caption" weight="semibold">
                      {item.label}
                    </Text>
                    <Text variant="caption" tone="secondary">
                      {item.rail}
                    </Text>
                  </Stack>
                </button>
              ))}
            </div>
          </Card>

          <Card tone="light">
            <Stack gap={1}>
              <Text variant="caption" weight="semibold">
                How activation works
              </Text>
              <Text variant="caption" tone="secondary">
                A first $50 deposit switches it on, and it lands in your account, not ours
              </Text>
            </Stack>
            <div className={`${checkout.reasons} pb-mt-2`}>
              {REASONS.map((reason) => (
                <div key={reason.title} className={checkout.reason}>
                  <Icon name={reason.icon} size={20} />
                  <Stack gap={1}>
                    <Text variant="caption" weight="semibold">
                      {reason.title}
                    </Text>
                    <Text variant="caption" tone="secondary">
                      {reason.body}
                    </Text>
                  </Stack>
                </div>
              ))}
            </div>
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">
                Due today
              </Text>
              <Pill tone="accent">Free to open</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-3">
              $0.00
            </Text>

            <div className={checkout.rule} />

            <div className={checkout.line}>
              <Text variant="caption" tone="onDarkMuted">
                Account
              </Text>
              <Text variant="caption" weight="semibold" tone="onDark">
                {option?.label}
              </Text>
            </div>
            <div className={checkout.line}>
              <Text variant="caption" tone="onDarkMuted">
                Account opening
              </Text>
              <Text variant="caption" weight="semibold" tone="accent">
                Free
              </Text>
            </div>
            <div className={checkout.line}>
              <Text variant="caption" tone="onDarkMuted">
                First deposit
              </Text>
              <Text variant="caption" weight="semibold" tone="onDark" numeric>
                $50 at activation
              </Text>
            </div>
            <div className={checkout.line}>
              <Text variant="caption" tone="onDarkMuted">
                Ready in
              </Text>
              <Text variant="caption" weight="semibold" tone="onDark">
                About 2 minutes
              </Text>
            </div>

            <Card.Footer>
              <Button size="lg" block iconEnd="arrow_forward" to="/open/account/done">
                Open account for free
              </Button>
            </Card.Footer>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              What you pay after this
            </Text>
            <Stack gap={0} className="pb-mt-2">
              {AFTER.map((row) => (
                <div key={row.label} className={checkout.line}>
                  <Text variant="caption" tone="secondary">
                    {row.label}
                  </Text>
                  <Text variant="caption" weight="semibold" numeric>
                    {row.value}
                  </Text>
                </div>
              ))}
            </Stack>
          </Card>
        </Stack>
      </div>
    </>
  )
}
